import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Clock, Video } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import { useI18n } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function nextWeekdays(count = 10) {
    const days = [];
    const d = new Date();
    while (days.length < count) {
        d.setDate(d.getDate() + 1);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days.push(new Date(d));
    }
    return days;
}

const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingSection() {
    const { t, lang } = useI18n();
    const days = useMemo(() => nextWeekdays(10), []);
    const [date, setDate] = useState(() => toISODate(days[0]));
    const [slots, setSlots] = useState(null);
    const [slot, setSlot] = useState("");
    const [form, setForm] = useState({ name: "", email: "", phone: "", consultation_type: "property", market: "", notes: "" });
    const [busy, setBusy] = useState(false);

    const loadSlots = useCallback(async () => {
        setSlots(null);
        setSlot("");
        try {
            const res = await fetch(`${API}/consultations/slots?date=${date}`);
            const data = await res.json();
            setSlots(data.available || []);
        } catch (_) {
            setSlots([]);
        }
    }, [date]);
    useEffect(() => { loadSlots(); }, [loadSlots]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !slot) {
            toast.error(t("booking.toast.fill"));
            return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API}/consultations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, market: form.consultation_type === "property" ? form.market || null : null, date, time_slot: slot, language: lang, website: "" }),
            });
            if (res.status === 409) {
                toast.error(t("booking.toast.taken"));
                loadSlots();
                return;
            }
            if (res.status === 429) {
                toast.error(t("booking.toast.rate_limit"));
                return;
            }
            if (!res.ok) throw new Error();
            toast.success(t("booking.toast.success_title"), { description: t("booking.toast.success_body") });
            setForm({ name: "", email: "", phone: "", consultation_type: "property", market: "", notes: "" });
            loadSlots();
        } catch (_) {
            toast.error(t("booking.toast.error"));
        } finally {
            setBusy(false);
        }
    };

    const dayLabel = (d) =>
        d.toLocaleDateString(lang === "pl" ? "pl-PL" : "en-GB", { weekday: "short", day: "numeric", month: "short" });

    const inputCls = "w-full bg-[#0A1A38]/60 border border-[#C9A96E]/25 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#C9A96E]/70 rounded-sm px-4 py-3 text-sm";

    return (
        <section id="booking" data-testid="booking-section" className="relative py-28 md:py-36 bg-[#07142B]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal max-w-3xl">
                    <span className="eyebrow">{t("booking.eyebrow")}</span>
                    <h2 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
                        {t("booking.title_a")} <em className="not-italic text-[#C9A96E]">{t("booking.title_highlight")}</em>
                    </h2>
                    <p className="mt-8 text-slate-400 max-w-2xl leading-relaxed">{t("booking.subtitle")}</p>
                </div>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-2 space-y-8">
                        <div className="reveal">
                            <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#C9A96E] mb-4">
                                <CalendarDays size={13} /> {t("booking.pick_date")}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {days.map((d) => {
                                    const iso = toISODate(d);
                                    return (
                                        <button
                                            key={iso}
                                            type="button"
                                            data-testid={`booking-date-${iso}`}
                                            onClick={() => setDate(iso)}
                                            className={`px-3 py-2.5 text-xs tracking-wide border rounded-sm transition ${
                                                date === iso
                                                    ? "border-[#C9A96E] bg-[#C9A96E] text-[#050D1C]"
                                                    : "border-[#C9A96E]/25 text-slate-300 hover:border-[#C9A96E]/60"
                                            }`}
                                        >
                                            {dayLabel(d)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="reveal">
                            <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#C9A96E] mb-4">
                                <Clock size={13} /> {t("booking.pick_slot")} <span className="text-slate-500 normal-case tracking-normal">(CET)</span>
                            </div>
                            {slots === null && <div className="text-slate-500 text-sm">{t("booking.loading_slots")}</div>}
                            {slots !== null && slots.length === 0 && (
                                <div data-testid="booking-no-slots" className="text-slate-500 text-sm">{t("booking.no_slots")}</div>
                            )}
                            {slots !== null && slots.length > 0 && (
                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="booking-slot-grid">
                                    {slots.map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            data-testid={`booking-slot-${s.replace(":", "")}`}
                                            onClick={() => setSlot(s)}
                                            className={`px-2 py-2 text-xs border rounded-sm transition ${
                                                slot === s
                                                    ? "border-[#C9A96E] bg-[#C9A96E] text-[#050D1C]"
                                                    : "border-[#C9A96E]/25 text-slate-300 hover:border-[#C9A96E]/60"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="reveal glass-card p-5 flex items-start gap-3 text-sm text-slate-400">
                            <Video size={18} className="text-[#C9A96E] mt-0.5 shrink-0" />
                            <span>{t("booking.video_note")}</span>
                        </div>
                    </div>

                    <form onSubmit={submit} data-testid="booking-form" className="lg:col-span-3 reveal glass-card p-8 md:p-10 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">{t("booking.fields.name")} *</label>
                                <input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Anna Kowalska" className={inputCls} />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">{t("booking.fields.email")} *</label>
                                <input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">{t("booking.fields.phone")}</label>
                                <input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="+48 ..." className={inputCls} />
                            </div>
                            <div>
                                <label className="block text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">{t("booking.fields.type")} *</label>
                                <select data-testid="booking-type" value={form.consultation_type} onChange={set("consultation_type")} className={inputCls}>
                                    <option value="property">{t("booking.types.property")}</option>
                                    <option value="concierge">{t("booking.types.concierge")}</option>
                                    <option value="events">{t("booking.types.events")}</option>
                                </select>
                            </div>
                            {form.consultation_type === "property" && (
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">{t("booking.fields.market")}</label>
                                    <select data-testid="booking-market" value={form.market} onChange={set("market")} className={inputCls}>
                                        <option value="">{t("booking.fields.market_any")}</option>
                                        {BRAND.markets.map((m) => (
                                            <option key={m.id} value={m.id}>{t(`markets.countries.${m.id}.name`)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="md:col-span-2">
                                <label className="block text-[10px] tracking-[0.24em] uppercase text-slate-400 mb-2">{t("booking.fields.notes")}</label>
                                <textarea data-testid="booking-notes" value={form.notes} onChange={set("notes")} rows={4} placeholder={t("booking.placeholders.notes")} className={inputCls} />
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                            <button data-testid="booking-submit" type="submit" disabled={busy || !slot} className="btn-gold justify-center disabled:opacity-50">
                                {busy ? t("booking.submitting") : t("booking.submit")}
                            </button>
                            <span data-testid="booking-selection-summary" className="text-xs text-slate-500 tracking-wide">
                                {slot ? `${date} · ${slot} CET` : t("booking.select_hint")}
                            </span>
                        </div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-600">{t("booking.disclaimer")}</p>
                    </form>
                </div>
            </div>
        </section>
    );
}
